import React from "react";
import { Link } from "react-router-dom";

export default function HomePage() {
  return (
    <div className="intro center">
      <h1>
        Making your vote <span className="red">count</span>
      </h1>

      <p>
        <b>
          Our elections are clearly ineffective, with the results seldom
          reflecting the will of the most people.
        </b>{" "}
        Many even say it’s rigged — with gerrymandering, voter suppression, and
        voter fraud.
      </p>
      <p>
        But arguably it’s the <i>way</i> we vote that causes more problems than
        any other aspect of our elections. Our “choose-one” method (called{" "}
        <b>plurality voting</b>) is unrepresentative, inaccurate, and unfair due
        to vote-splitting.
      </p>
      <p>
        But what if there’s a better way, a more intelligent way, a way of
        calculating results that’s more likely to lead to the selection of
        highly qualified candidates who best reflect the will of the voters?
      </p>
      <p>
        Click the button below to try four different methods of tallying
        results.
      </p>
      <div className="center callToAction">
        <Link className="bigButton" to="/demo/bidenvp">
          Who should Joe Biden pick for VP?
        </Link>
      </div>
      <h2>What’s wrong with plurality voting?</h2>
      <p>
        In plurality voting, we each vote for our favorite candidate and the
        candidate with the most votes wins.
      </p>
      <p>
        Plurality voting is fine for deciding between two candidates. However,
        it doesn’t work well in elections with three or more candidates, as you
        might encounter in a party primary, when there are several candidates,
        and a very good chance that the best won’t win.
      </p>
      <p>
        To illustrate just one of several problems with this method, Consider
        the following scenario.
      </p>
      <img width="100%" src="stick-figures.png" alt="Vote Splitting" />
      <p>
        <img
          style={{ marginTop: "0px" }}
          className="hat"
          src="bighat.png"
          alt="Big Hat"
        />
        In an election between one <span className="hat">big&nbsp;hat</span>{" "}
        candidate and one <span className="hat">small&nbsp;hat</span> candidate,
        the <span className="hat">big&nbsp;hat</span> candidate wins easily.
        People prefer <span className="hat">big&nbsp;hats</span>.
      </p>

      <p>
        <img
          style={{ marginTop: "-3px" }}
          className="hat"
          src="bighat2.png"
          alt="Small Hat"
        />
        But, what if a second candidate enters the race with a nearly identical{" "}
        <span className="hat">big&nbsp;hat</span>, thus neatly splitting the{" "}
        <span className="hat">big&nbsp;hat</span> coalition?
      </p>
      <p>
        <img
          style={{ marginTop: "-6px" }}
          className="hat"
          src="smallhat.png"
          alt="Big Hat"
        />
        Most people wanted a <span className="hat">big&nbsp;hat</span>, but they
        ended up with a <span className="hat">small&nbsp;hat</span>, not because{" "}
        <span className="hat">small&nbsp;hats</span> are more popular, but
        because the <span className="hat">big&nbsp;hats</span> split the
        majority vote.
      </p>

      <h2>How do we ensure that the better hat wins?</h2>
      <p>
        Well, let’s try an experiment, but instead of hats, we’ll use human
        beings, specifically a group of human beings that Joe Biden might be
        inclined to choose as his running mate.
      </p>
      <p>
        You’ll cast your vote using each of the leading voting methods. Each has
        different degrees of expressiveness and simplicity. See how each one
        feels, then after the votes are all in we’ll compare the results.
      </p>
      <div className="center callToAction">
        <Link className="bigButton" to="/demo/bidenvp">
          Who should Joe Biden pick for VP?
        </Link>
        <p>
          <b>Vote Now!</b>{" "}
          <i>
            This voting experiment ends once Biden announces his running mate,
            which he's said will be on or before August 1<sup>st</sup>
          </i>
        </p>
      </div>
      <p>
        Please play along and cast your vote as if this was a real election. It
        will make the results much more meaningful to analyze and discuss. And,
        who knows — maybe we can even influence Joe's decision! Let’s find out
        if different methods pick different winners and discuss what that
        reveals about the strengths and weaknesses of each method:
      </p>
      <div>
        <table className="methods center">
          <tbody>
            <tr>
              <td>
                <b>Approval Voting</b>
              </td>
              <td>
                {" "}
                <a href="https://www.electionscience.org/" alt="">
                  The Center for Election Science
                </a>
              </td>
            </tr>
            <tr>
              <td>
                <b>STAR Voting</b>
              </td>
              <td>
                <a href="https://www.equal.vote/" alt="">
                  The Equal Vote Coalition
                </a>
              </td>
            </tr>
            <tr>
              <td>
                <b>Ranked Choice Voting</b>
              </td>
              <td>
                <a href="https://www.fairvote.org/" alt="">
                  FairVote
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        After casting your votes, you'll have the option of registering to
        attend a live panel discussion of the election results.
      </p>

      <p>&nbsp;</p>
    </div>
  );
}
